import { ref, onMounted, onUnmounted, computed, nextTick } from "vue";
import { debounce } from "tir-pm-debounce";

/**
 * Опции для composable useReverseInfiniteScroll
 */
export interface UseReverseInfiniteScrollOptions {
  /**
   * Функция загрузки данных.
   * Принимает номер страницы и загружает данные.
   * Должна возвращать `true`, если данные загружены, и `false`, если больше данных нет.
   */
  loadMore: (page: number) => Promise<boolean>;
  /** Отступ от верха, при котором срабатывает подгрузка (в пикселях) */
  topIndent?: number;
  /** Контейнер, у которого отслеживается скролл (по умолчанию `window`) */
  scrollContainer?: () => HTMLElement | Window;
}

/**
 * Composable для бесконечной подгрузки данных при прокрутке вверх (например, для чатов).
 * Загружаемые данные вставляются в начало списка.
 */
export function useReverseInfiniteScroll({
  loadMore,
  topIndent = 100,
  scrollContainer = () => window,
}: UseReverseInfiniteScrollOptions) {
  /** Флаг, загружены ли все данные (достигнуто начало истории) */
  const isAllLoaded = ref(false);
  /** Флаг, идет ли загрузка данных */
  const isLoading = ref(false);
  /** Текущая страница */
  const currentPage = ref(1);

  /**
   * Проверяет, достигнут ли верх списка (верхний отступ <= topIndent)
   */
  const isScrollTop = () => {
    const container = scrollContainer();
    if (!container) return false;

    if (container instanceof Window) {
      return window.scrollY <= topIndent;
    } else {
      return container.scrollTop <= topIndent;
    }
  };

  const loadPage = async () => {
    if (isAllLoaded.value || isLoading.value) return;

    const container = scrollContainer();
    const prevScrollTop =
      container instanceof Window ? window.scrollY || window.pageYOffset : container.scrollTop;
    const prevScrollHeight =
      container instanceof Window ? document.documentElement.scrollHeight : container.scrollHeight;

    isLoading.value = true;
    const hasMoreData = await loadMore(currentPage.value);
    isLoading.value = false;

    if (!hasMoreData) {
      isAllLoaded.value = true;
      return;
    }

    currentPage.value++;
    await nextTick(); // Ждём обновления DOM

    const newScrollHeight =
      container instanceof Window ? document.documentElement.scrollHeight : container.scrollHeight;

    // Вычисляем, насколько увеличился контент
    const scrollOffset = newScrollHeight - prevScrollHeight;

    if (container instanceof Window) {
      window.scrollTo({ top: prevScrollTop + scrollOffset });
    } else {
      container.scrollTop = prevScrollTop + scrollOffset;
    }

    // Если после коррекции мы всё ещё вверху, подгружаем ещё
    if (isScrollTop()) {
      loadPage();
    }
  };

  /**
   * Сбрасывает пагинацию на первую страницу
   */
  const resetPagination = async () => {
    isAllLoaded.value = false;
    isLoading.value = false;
    currentPage.value = 1;
    await loadMore(1); // Загружаем первую порцию данных
  };

  /**
   * Обработчик скролла с дебаунсом
   */
  const onScroll = debounce(() => {
    if (!isAllLoaded.value && !isLoading.value && isScrollTop()) {
      loadPage();
    }
  }, 10);

  /**
   * Навешиваем обработчик скролла и загружаем первую порцию данных
   */
  onMounted(() => {
    const container = scrollContainer();
    if (container) {
      container.addEventListener("scroll", onScroll);
      loadPage();
    }
  });

  /**
   * Убираем обработчик скролла при размонтировании
   */
  onUnmounted(() => {
    const container = scrollContainer();
    if (container) {
      container.removeEventListener("scroll", onScroll);
    }
  });

  return {
    isLoading: computed(() => isLoading.value),
    isAllLoaded,
    loadPage,
    resetPagination,
  };
}
